/**
 * Modal de disposición post-llamada (After-Call Work / Wrap-up).
 * Aparece automáticamente cuando termina una llamada para que el agente
 * clasifique el resultado antes de volver a estado "available".
 *
 * Si elige "callback" muestra campos de fecha/hora para agendar la rellamada.
 */

import { useCallback, useState } from 'react';
import {
  Modal,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Text,
  Badge,
  Box,
} from '@/lib/polaris';
import {
  CALL_DISPOSITION_LABELS,
  type CallDisposition,
} from '@/types/callCenter.types';

const DISPOSITION_OPTIONS: CallDisposition[] = [
  'venta_confirmada',
  'interesado',
  'callback',
  'no_contesta',
  'buzon_voz',
  'rechazo_pedido',
  'numero_equivocado',
  'ocupado',
  'otro',
];

const DISPOSITION_TONES: Partial<Record<CallDisposition, 'success' | 'warning' | 'critical' | 'info' | 'attention'>> = {
  venta_confirmada: 'success',
  interesado: 'info',
  callback: 'attention',
  rechazo_pedido: 'critical',
  numero_equivocado: 'warning',
};

export interface CallDispositionResult {
  disposition: CallDisposition;
  notes: string;
  /** ISO timestamp si es callback. */
  callbackAt: string | null;
}

interface CallDispositionModalProps {
  open: boolean;
  remotePhone: string | null;
  leadName: string | null;
  durationSeconds: number;
  onSubmit: (result: CallDispositionResult) => void;
  onSkip: () => void;
  submitting?: boolean;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function defaultTimeStr() {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function CallDispositionModal({
  open,
  remotePhone,
  leadName,
  durationSeconds,
  onSubmit,
  onSkip,
  submitting = false,
}: CallDispositionModalProps) {
  const [selected, setSelected] = useState<CallDisposition | null>(null);
  const [notes, setNotes] = useState('');
  const [cbDate, setCbDate] = useState(todayStr);
  const [cbTime, setCbTime] = useState(defaultTimeStr);

  const isCallback = selected === 'callback';

  const handleSubmit = useCallback(() => {
    if (!selected) return;
    let callbackAt: string | null = null;
    if (isCallback && cbDate && cbTime) {
      callbackAt = new Date(`${cbDate}T${cbTime}:00`).toISOString();
    }
    onSubmit({ disposition: selected, notes: notes.trim(), callbackAt });
  }, [selected, notes, isCallback, cbDate, cbTime, onSubmit]);

  const durationLabel = durationSeconds > 0
    ? `${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, '0')}`
    : '0:00';

  return (
    <Modal
      open={open}
      onClose={onSkip}
      title="Disposición de llamada"
      primaryAction={{
        content: isCallback ? 'Agendar y guardar' : 'Guardar',
        onAction: handleSubmit,
        disabled: !selected || (isCallback && (!cbDate || !cbTime)),
        loading: submitting,
      }}
      secondaryActions={[
        {
          content: 'Omitir',
          onAction: onSkip,
          disabled: submitting,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <InlineStack gap="300" align="start" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              {leadName ?? remotePhone ?? 'Desconocido'} · {durationLabel}
            </Text>
          </InlineStack>

          <Text as="p" variant="bodyMd" fontWeight="semibold">
            ¿Cuál fue el resultado de la llamada?
          </Text>

          <InlineStack gap="200" wrap>
            {DISPOSITION_OPTIONS.map((key) => (
              <Button
                key={key}
                variant={selected === key ? 'primary' : 'secondary'}
                size="slim"
                onClick={() => setSelected(key)}
                tone={selected === key ? (DISPOSITION_TONES[key] === 'critical' ? 'critical' : undefined) : undefined}
              >
                {selected === key ? '✓ ' : ''}{CALL_DISPOSITION_LABELS[key]}
              </Button>
            ))}
          </InlineStack>

          {selected === 'venta_confirmada' ? (
            <Badge tone="success">Excelente — venta registrada</Badge>
          ) : null}

          {isCallback ? (
            <Box padding="300" background="bg-surface-secondary" borderRadius="200">
              <BlockStack gap="300">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  Agendar rellamada
                </Text>
                <InlineStack gap="300" wrap={false}>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="Fecha"
                      type="date"
                      value={cbDate}
                      onChange={setCbDate}
                      autoComplete="off"
                      min={todayStr()}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="Hora"
                      type="time"
                      value={cbTime}
                      onChange={setCbTime}
                      autoComplete="off"
                    />
                  </div>
                </InlineStack>
              </BlockStack>
            </Box>
          ) : null}

          <TextField
            label="Notas (opcional)"
            value={notes}
            onChange={setNotes}
            multiline={3}
            autoComplete="off"
            placeholder="Detalles adicionales sobre la llamada…"
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
